<template>
  <transition name="bt-fade-scale" appear>
    <button
      v-show="visible"
      class="back-to-top"
      :class="{ dark: isDark }"
      @click="scrollToTop"
      :title="title"
      aria-label="返回顶部"
    >
      <i class="fa-solid fa-arrow-up"></i>
    </button>
  </transition>
</template>

<script>
export default {
  name: 'BackToTop',
  props: {
    threshold: { type: Number, default: 300 },
    title: { type: String, default: '返回顶部' }
  },
  data() {
    return {
      visible: false,
      scrollTargets: [],
      pollTimer: null,
      pollCounter: 0,
      isDark: false,
      themeObserver: null
    }
  },
  watch: {
    // 路由变化时重新检测滚动容器，确保在页面切换后仍能显示按钮
    $route() {
      this.$nextTick(() => this.refreshScrollTargets());
    }
  },
  mounted() {
    // 初始化夜间模式状态并监听主题变化
    this.isDark = document.body.classList.contains('dark-theme');
    try {
      this.themeObserver = new MutationObserver(() => {
        this.isDark = document.body.classList.contains('dark-theme');
      });
      this.themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    } catch (e) { void 0; }

    this.detectScrollTargets();
    this.onScroll();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    // 监听内部滚动容器
    this.scrollTargets.forEach(el => el.addEventListener('scroll', this.onScroll, { passive: true }));
    // 轮询兜底（应对部分容器不触发事件的场景）
    this.pollTimer = setInterval(() => {
      this.onScroll();
      this.pollCounter = (this.pollCounter + 1) % 10;
      if (this.pollCounter === 0) {
        this.refreshScrollTargets();
      }
    }, 1000);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    this.scrollTargets.forEach(el => el.removeEventListener('scroll', this.onScroll));
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.themeObserver) {
      try { this.themeObserver.disconnect(); } catch (e) { void 0; }
      this.themeObserver = null;
    }
  },
  methods: {
    refreshScrollTargets() {
      // 移除旧监听
      this.scrollTargets.forEach(el => el.removeEventListener('scroll', this.onScroll));
      // 重新检测并绑定
      this.detectScrollTargets();
      this.scrollTargets.forEach(el => el.addEventListener('scroll', this.onScroll, { passive: true }));
      // 强制刷新可见性
      this.onScroll();
    },
    detectScrollTargets() {
      try {
        const candidates = Array.from(document.querySelectorAll('body *'));
        const targets = candidates.filter(el => {
          const style = getComputedStyle(el);
          const isScrollableY = ['auto', 'scroll'].includes(style.overflowY);
          return isScrollableY && el.scrollHeight > el.clientHeight;
        });
        // 去重并保存
        const set = new Set();
        this.scrollTargets = targets.filter(el => {
          const key = el.tagName + (el.className || '') + (el.id || '');
          if (set.has(key)) return false;
          set.add(key);
          return true;
        });
      } catch (e) {
        this.scrollTargets = [];
      }
    },
    getMaxScrollTop() {
      const windowScroll = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      const innerScroll = this.scrollTargets.reduce((max, el) => Math.max(max, el.scrollTop || 0), 0);
      return Math.max(windowScroll, innerScroll);
    },
    onScroll() {
      this.visible = this.getMaxScrollTop() > this.threshold;
    },
    scrollToTop() {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (e) {
        window.scrollTo(0, 0);
      }
      // 同时滚动内部容器到顶部
      this.scrollTargets.forEach(el => {
        try { el.scrollTo({ top: 0, behavior: 'smooth' }); }
        catch { el.scrollTop = 0; }
      });
    }
  }
}
</script>

<style scoped>
.back-to-top {
  position: fixed;
  right: 0;
  bottom: 60px;
  width: 44px;
  height: 40px;
  border-radius: 30px 0 0 30px; /* C型：左侧圆角，右侧直角贴边 */
  border: none;
  background: rgba(74, 144, 226, 0.9);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: -4px 4px 12px rgba(0,0,0,0.2);
  cursor: pointer;
  z-index: var(--z-floating);
  transition: transform 0.2s ease, opacity 0.2s ease, background 0.2s ease;
}
.back-to-top:hover {
  transform: translateX(-4px);
  background: rgba(58, 120, 200, 0.95);
}
.back-to-top i {
  font-size: 18px;
}

/* 夜间模式样式（通过动态类保证在 scoped 下也生效） */
.back-to-top.dark {
  background: rgba(35, 35, 35, 0.92);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.14);
  box-shadow: 0 4px 14px rgba(0,0,0,0.45);
}
.back-to-top.dark:hover {
  background: rgba(28, 28, 28, 0.96);
}

/* 淡入淡出 + 轻微缩放动画 */
.bt-fade-scale-enter-active, .bt-fade-scale-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.bt-fade-scale-enter-from, .bt-fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.92);
}

@media (max-width: 768px) {
  .back-to-top {
    bottom: 90px; /* 避开底部导航栏 */
    right: 0;
    width: 35px;
    height: 35px;
    border-radius: 22px 0 0 22px;
    box-shadow: -4px 4px 12px rgba(0,0,0,0.2);
  }
  .back-to-top:hover {
    transform: translateX(-4px);
  }
}
@media (max-width: 480px) {
  .back-to-top {
    bottom: 85px;
    width: 35px;
    height: 35px;
  }
}
</style>